import { Form, Formik } from 'formik';
import NextLink from 'next/link';
import { useContext, useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { CONTAINER_WIDTHS } from '@hookdeck/theme';

import LINKS from '../../../configs/links';
import { isMultiFactorRequired } from '../../../typings/MultiFactor.interface';
import { capitalizeFirstLetter, stringifyQuery } from '../../../utils';
import AuthSensitivePageHead from '../../common/AuthSensitivePageHead';
import Alert from '../../common/base/Alert';
import Badge from '../../common/base/Badge';
import Button, { ClickableArea } from '../../common/base/Button';
import Divider from '../../common/base/Divider';
import Icon from '../../common/base/Icon';
import Link from '../../common/base/Link';
import Text from '../../common/base/Text';
import TextInput from '../../common/Form/Fields/TextInput';
import { Div } from '../../common/helpers/StyledUtils';
import { GlobalContext } from '../../contexts/GlobalContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import { completeAuthRedirect } from '../../../utils/completeAuthRedirect';

// Full page container with tan background
const StyledSigninContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    width: 100%;
    background-color: ${theme.colors.surface.base.background};
    position: relative;
    overflow: hidden;
  `,
);

// Background decoration (pipes)
const StyledBackgroundDecoration = styled.div(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    pointer-events: none;
    //background: url('/images/signin-full-pipes-${theme.mode}.svg') no-repeat center center;
    background: url('/images/wave-${theme.mode}.svg') no-repeat center center;
    background-size: cover;
    opacity: 1;
    z-index: 0;
  `,
);

// Centered content wrapper
const StyledContentWrapper = styled.div(
  ({ theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(8)};
    position: relative;
    z-index: 1;
  `,
);

// Main card
const StyledCard = styled.div(
  ({ theme }) => css`
    background-color: ${theme.colors.surface.base.surface};
    border-radius: ${theme.radius.large};
    box-shadow: ${theme.elevation[2]};
    padding: ${theme.spacing(8)};
    width: ${theme.pxToRem(CONTAINER_WIDTHS.small)};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
  `,
);

// Header section with logo and title
const StyledHeader = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(6)};
    text-align: center;
  `,
);

// OAuth section
const StyledOAuthSection = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
);

// OAuth button wrapper for positioning badge
const StyledOAuthButtonWrapper = styled.div`
  position: relative;
`;

const StyledLastUsedBadge = styled.div(
  ({ theme }) => css`
    position: absolute;
    right: ${theme.spacing(2)};
    top: 0;
    transform: translateY(-50%);
  `,
);

// Form section
const StyledFormSection = styled.div`
  display: flex;
  flex-direction: column;
`;

// Actions section
const StyledActionsSection = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    align-items: center;
  `,
);

// Footer
const StyledFooter = styled.div(
  ({ theme }) => css`
    padding: ${theme.spacing(8)};
    display: flex;
    justify-content: center;
    text-align: center;
    z-index: 1;
  `,
);

interface SigninLayoutProps {
  default_error?: string;
  redirect?: string;
  organization_slug?: string;
}

const LAST_USED_PROVIDER_KEY = 'hookdeck_last_auth_provider';

const SigninLayout: React.FC<SigninLayoutProps> = ({
  default_error,
  redirect,
  organization_slug,
}) => {
  const [error, updateError] = useState<string | null>(default_error || null);
  const [sso, showSSO] = useState<boolean>(false);
  const { HookdeckAPI, configs } = useContext(GlobalContext);
  const theme = useTheme();

  const [is_redirecting, setRedirecting] = useState<boolean>(!!organization_slug);
  const [last_used_provider, setLastUsedProvider] = useLocalStorage<string | null>(
    LAST_USED_PROVIDER_KEY,
    null,
  );

  const [clicked_provider, setClickedProvider] = useState<boolean>(false);
  const handleClickProvider = (provider: string) => {
    setClickedProvider(true);
    setLastUsedProvider(provider);
  };

  const auth_base_path = `${HookdeckAPI.api_url}/signin`;

  const query: { redirect?: string } = {};
  if (redirect) {
    query.redirect = redirect;
  }

  const querystring = Object.keys(query).length > 0 ? `?${stringifyQuery(query)}` : '';

  const header_spacing = <Div h={{ px: 104 }}></Div>;

  const footer = (
    <StyledFooter>
      <Text neutral w={{ px: 300 }}>
        By signing in, you agree to our{' '}
        <Link href={LINKS.terms}>
          <Text neutral as="span" underline>
            Terms of Service
          </Text>
        </Link>{' '}
        and{' '}
        <Link href={LINKS.privacy}>
          <Text neutral as="span" underline>
            Privacy Policies
          </Text>
        </Link>
      </Text>
    </StyledFooter>
  );

  useEffect(() => {
    if (organization_slug) {
      HookdeckAPI.auth
        .getSSOUrl(undefined, organization_slug)
        .then(({ url }) => {
          if (url) {
            window.location.href = `${url}?${query}`;
          }
        })
        .catch(() => {
          setRedirecting(false);
          updateError('Failed to fetch SSO URL');
        });
    }
  }, [organization_slug]);

  if (organization_slug && is_redirecting) {
    return (
      <StyledSigninContainer>
        <StyledBackgroundDecoration />
        <StyledContentWrapper>
          <Text muted center>
            Redirecting to your SSO provider...
          </Text>
        </StyledContentWrapper>
      </StyledSigninContainer>
    );
  }

  // SSO form view
  if (sso || organization_slug) {
    return (
      <StyledSigninContainer>
        <StyledBackgroundDecoration />
        {header_spacing}
        <StyledContentWrapper>
          <StyledCard>
            <StyledHeader>
              <a href={LINKS.landing}>
                <img src={`/images/logo-colored-${theme.mode}.svg`} height="18" alt="Hookdeck" />
              </a>
              <Div>
                <Text bold size="xl" m={{ b: 1 }}>
                  Sign in to continue
                </Text>
                <Text muted>
                  Single Sign-On allows you to connect to Hookdeck using your organization's
                  identity provider.
                </Text>
              </Div>
            </StyledHeader>

            <Formik<{ email: string }>
              initialValues={{ email: '' }}
              validate={(values) => {
                const errors: { email?: string } = {};
                if (!values.email || values.email.length === 0) {
                  errors.email = 'Required';
                }
                return errors;
              }}
              onSubmit={async (v) => {
                return HookdeckAPI.auth
                  .getSSOUrl(v.email, organization_slug)
                  .then(
                    ({ url }) =>
                      (window.location.href = `${url}${querystring ? querystring.replace('?', '&') : ''}`),
                  )
                  .catch((e) => {
                    let err = 'Unexpected error';
                    if (e.response?.message) {
                      err = e.response?.message;
                    } else if (typeof e.response === 'string') {
                      err = e.response;
                    }
                    updateError(err);
                  });
              }}>
              <Form>
                <StyledFormSection>
                  <TextInput
                    label="Work Email"
                    name="email"
                    auto_complete="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                  />
                </StyledFormSection>

                <StyledActionsSection>
                  <Button submit large primary block>
                    Single Sign-On
                  </Button>
                  {(error || default_error) && (
                    <Alert inline danger style={{ width: '100%' }}>
                      {error || default_error}
                    </Alert>
                  )}
                  {!organization_slug && (
                    <Text muted>
                      <Link neutral onClick={() => showSSO(false)}>
                        Cancel
                      </Link>
                    </Text>
                  )}
                </StyledActionsSection>
              </Form>
            </Formik>
          </StyledCard>
        </StyledContentWrapper>
        {footer}
      </StyledSigninContainer>
    );
  }

  // Main signin form view
  return (
    <StyledSigninContainer>
      <AuthSensitivePageHead />
      <StyledBackgroundDecoration />
      {header_spacing}
      <StyledContentWrapper>
        <StyledCard>
          <StyledHeader>
            <a href={LINKS.landing}>
              <img src={`/images/logo-colored-${theme.mode}.svg`} height="18" alt="Hookdeck" />
            </a>
            <Div>
              <Text bold size="xl" m={{ b: 1 }}>
                Sign in to Hookdeck
              </Text>
              <Text muted>Welcome back! Sign in to continue.</Text>
            </Div>
          </StyledHeader>

          <Div>
            <StyledOAuthSection>
              <StyledOAuthButtonWrapper>
                <ClickableArea
                  flex={{ align: 'center', justify: 'center' }}
                  as="a"
                  href={`${auth_base_path}/github${querystring}`}
                  onClick={() => handleClickProvider('github')}
                  p={{ y: 1.5, x: 3 }}
                  rounded
                  style={{
                    boxShadow: theme.shadows.neutral.base,
                    borderRadius: theme.radius.normal,
                    gap: theme.spacing(2),
                  }}>
                  <Icon
                    src={`${configs.APP_URL}/images/github-icon.svg`}
                    style={{ filter: theme.mode === 'dark' ? 'invert(1)' : undefined }}
                  />
                  <Text as="span">
                    Sign in with <strong>Github</strong>
                  </Text>
                  {!clicked_provider && last_used_provider === 'github' && (
                    <StyledLastUsedBadge>
                      <Badge neutral>Last Used</Badge>
                    </StyledLastUsedBadge>
                  )}
                </ClickableArea>
              </StyledOAuthButtonWrapper>
              <StyledOAuthButtonWrapper>
                <ClickableArea
                  flex={{ align: 'center', justify: 'center' }}
                  as="a"
                  href={`${auth_base_path}/google${querystring}`}
                  onClick={() => handleClickProvider('google')}
                  p={{ y: 1.5, x: 3 }}
                  rounded
                  style={{
                    boxShadow: theme.shadows.neutral.base,
                    borderRadius: theme.radius.normal,
                    gap: theme.spacing(2),
                  }}>
                  <Icon src={`${configs.APP_URL}/images/google-icon.png`} />
                  <Text as="span">
                    Sign in with <strong>Google</strong>
                  </Text>
                  {!clicked_provider && last_used_provider === 'google' && (
                    <StyledLastUsedBadge>
                      <Badge neutral>Last Used</Badge>
                    </StyledLastUsedBadge>
                  )}
                </ClickableArea>
              </StyledOAuthButtonWrapper>
              <StyledOAuthButtonWrapper>
                <ClickableArea
                  onClick={() => {
                    handleClickProvider('sso');
                    showSSO(true);
                  }}
                  p={{ y: 1.5, x: 3 }}
                  rounded
                  flex={{ align: 'center', justify: 'center' }}
                  style={{
                    boxShadow: theme.shadows.neutral.base,
                    borderRadius: theme.radius.normal,
                    gap: theme.spacing(2),
                  }}>
                  <Icon icon="lock" />
                  <Text as="span">
                    Sign in with <strong>SSO</strong>
                  </Text>
                  {!clicked_provider && last_used_provider === 'sso' && (
                    <StyledLastUsedBadge>
                      <Badge neutral>Last Used</Badge>
                    </StyledLastUsedBadge>
                  )}
                </ClickableArea>
              </StyledOAuthButtonWrapper>
            </StyledOAuthSection>

            <Divider m={{ y: 8 }} />

            <Formik<{ email: string; password: string }>
              initialValues={{ email: '', password: '' }}
              validate={(values) => {
                const errors: { email?: string; password?: string } = {};
                if (!values.email || values.email.length === 0) {
                  errors.email = 'Required';
                }
                if (!values.password || values.password.length === 0) {
                  errors.password = 'Required';
                } else if (values.password.length < 8) {
                  errors.password = 'Password must contain at least 8 characters';
                }
                return errors;
              }}
              onSubmit={(v) => {
                handleClickProvider('email');
                HookdeckAPI.auth
                  .signin({ email: v.email, password: v.password }, query)
                  .then(async (r) => {
                    if (isMultiFactorRequired(r)) {
                      // Redirect to MFA verification page with token
                      (window as any).location = `/signin/mfa?token=${r.multi_factor_token}`;
                    } else if (r.redirect) {
                      await completeAuthRedirect(HookdeckAPI, configs.ENV, r.redirect);
                    } else {
                      await completeAuthRedirect(HookdeckAPI, configs.ENV);
                    }
                  })
                  .catch((e) => {
                    let err = 'Unexpected error';
                    if (e.response?.message) {
                      err = e.response?.message;
                    } else if (typeof e.response === 'string') {
                      err = e.response;
                    } else if (e.response?.data?.[0]) {
                      err = capitalizeFirstLetter(e.response?.data?.[0]);
                    }
                    updateError(err);
                  });
              }}>
              <Form>
                <StyledFormSection>
                  <TextInput
                    label="Email"
                    name="email"
                    auto_complete="email"
                    type="email"
                    placeholder="you@example.com"
                  />
                  <TextInput
                    type="password"
                    label="Password"
                    name="password"
                    auto_complete="current-password"
                    maxlength={255}
                    minlength={8}
                    m={0}
                    help={
                      <NextLink legacyBehavior passHref href="/reset-password">
                        <Link neutral underline font_weight={theme.font_weights.regular}>
                          Forgot password?
                        </Link>
                      </NextLink>
                    }
                  />
                </StyledFormSection>

                <StyledActionsSection>
                  <Button submit large primary block m={{ t: 8 }}>
                    Sign in
                  </Button>
                  {(error || default_error) && (
                    <Alert inline danger style={{ width: '100%' }}>
                      {error || default_error}
                    </Alert>
                  )}
                  <Text muted>
                    Don't have an account?{' '}
                    <NextLink legacyBehavior passHref href={`/signup${querystring}`}>
                      <Link>Sign up</Link>
                    </NextLink>
                  </Text>
                </StyledActionsSection>
              </Form>
            </Formik>
          </Div>
        </StyledCard>
      </StyledContentWrapper>
      {footer}
    </StyledSigninContainer>
  );
};

export default SigninLayout;
